import { searchApi, type SearchRow } from "@/services/api/search";
import { inputMethodManager } from "@/utils/pinyin";
import { useCallback, useEffect, useRef, useState } from "react";

export interface UseSearchPanelResult {
  keyword: string;
  setKeyword: (val: string) => void;
  rows: SearchRow[];
  isLoading: boolean;
  error: string | null;
  hasSearchResults: boolean;
  search: (kw?: string) => Promise<SearchRow[] | undefined>;
  clearResults: () => void;
  clearCache: () => void;
}

export function useSearchPanelOptimized(
  initialKeyword: string = ""
): UseSearchPanelResult {
  const [keyword, setKeyword] = useState<string>(initialKeyword);
  const [rows, setRows] = useState<SearchRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearchResults, setHasSearchResults] = useState<boolean>(false);
  const abortRef = useRef<AbortController | null>(null);

  // 优化缓存策略 - 使用LRU缓存
  const cacheRef = useRef<
    Map<string, { data: SearchRow[]; timestamp: number }>
  >(new Map());
  const maxCacheSize = 20;
  const cacheExpiry = 5 * 60 * 1000; // 5分钟过期

  // LRU缓存管理
  const getCachedResult = useCallback((key: string): SearchRow[] | null => {
    const cached = cacheRef.current.get(key);
    if (!cached) return null;

    // 检查是否过期
    if (Date.now() - cached.timestamp > cacheExpiry) {
      cacheRef.current.delete(key);
      return null;
    }

    // 更新访问时间（LRU）
    cacheRef.current.delete(key);
    cacheRef.current.set(key, { ...cached, timestamp: Date.now() });
    return cached.data;
  }, []);

  const setCachedResult = useCallback((key: string, data: SearchRow[]) => {
    // 如果缓存已满，删除最旧的条目
    if (cacheRef.current.size >= maxCacheSize) {
      const firstKey = cacheRef.current.keys().next().value;
      if (firstKey) {
        cacheRef.current.delete(firstKey);
      }
    }

    cacheRef.current.set(key, { data, timestamp: Date.now() });
  }, []);

  // 优化搜索函数 - 减少重复调用
  const search = useCallback(
    async (kw?: string) => {
      const q = (kw || keyword || "").trim();
      console.log("🚀 执行搜索:", { q, keyword, kw });

      if (!q) {
        setRows([]);
        setHasSearchResults(false);
        return [];
      }

      // 快速缓存命中 - 避免返回缓存的空结果
      const cached = getCachedResult(q);
      if (cached && cached.length > 0) {
        console.log("⚡ 缓存命中:", cached);
        setRows(cached);
        setHasSearchResults(cached.length > 0);
        return cached;
      }

      setIsLoading(true);
      setError(null);

      try {
        // 取消上一请求
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        console.log("🌐 调用API搜索:", q);
        const res = await searchApi.search(q, controller.signal);
        console.log("📊 搜索结果:", res);

        // 避免缓存空结果，只有当有数据时才缓存
        if (res.items && res.items.length > 0) {
          setCachedResult(q, res.items);
        }
        setRows(res.items);
        setHasSearchResults(res.items.length > 0);
        return res.items;
      } catch (e: any) {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
          console.log("🚫 搜索请求已取消");
          return;
        }
        console.error("❌ 搜索错误:", e);
        setError(e?.message ?? "搜索失败");
      } finally {
        setIsLoading(false);
      }
    },
    [keyword, getCachedResult, setCachedResult]
  );

  // 优化防抖逻辑
  const debounceTimeouts = useRef<Map<string, number>>(new Map());

  const debouncedSearch = useCallback(
    (q: string) => {
      // 清除之前的定时器
      const existingTimeout = debounceTimeouts.current.get(q);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      // 动态调整防抖时间
      let debounceTime = 120; // 基础防抖时间

      // 根据设备类型调整
      if (window.innerWidth < 1024) {
        debounceTime += 30; // 移动端稍微增加
      }

      // 根据输入长度调整
      if (q.length <= 2) {
        debounceTime += 50; // 短输入增加防抖
      }

      const timeoutId = setTimeout(() => {
        search(q);
        debounceTimeouts.current.delete(q);
      }, debounceTime);

      debounceTimeouts.current.set(q, timeoutId as unknown as number);
    },
    [search]
  );

  // 监听关键字变化，智能触发搜索
  useEffect(() => {
    const q = keyword.trim();
    if (!q) {
      setRows([]);
      setHasSearchResults(false);
      return;
    }

    // 检查是否应该触发搜索
    const shouldTrigger = inputMethodManager.shouldTriggerSearch(q);

    if (!shouldTrigger) {
      return;
    }

    // 使用优化的防抖搜索
    debouncedSearch(q);

    // 清理函数
    return () => {
      const timeoutId = debounceTimeouts.current.get(q);
      if (timeoutId) {
        clearTimeout(timeoutId);
        debounceTimeouts.current.delete(q);
      }
    };
  }, [keyword, debouncedSearch]);

  const clearResults = useCallback(() => {
    setRows([]);
    setHasSearchResults(false);
    setError(null);
  }, []);

  const clearCache = useCallback(() => {
    cacheRef.current.clear();
    console.log("🗑️ 缓存已清空");
  }, []);

  // 清理定时器
  useEffect(() => {
    return () => {
      debounceTimeouts.current.forEach((timeoutId) => clearTimeout(timeoutId));
      debounceTimeouts.current.clear();
    };
  }, []);

  return {
    keyword,
    setKeyword,
    rows,
    isLoading,
    error,
    hasSearchResults,
    search,
    clearResults,
    clearCache,
  };
}

export default useSearchPanelOptimized;
